/**
 * 地図 API が返す物件データ
 * @typedef {Object} Property
 * @property {number|string} id - 物件ID
 * @property {string} category_type - 物件種別（consts.js の CATEGORIES の値）
 * @property {{ lat: number, lng: number }} geopoint - 物件の座標
 */

/**
 * 表示範囲内の物件データを取得する。中断用に signal を受ける
 * @param {Object} params
 * @param {string} [params.type="all"] - 物件種別（CATEGORIES の値 または "all"）
 * @param {number} params.latFrom - 南西端の緯度
 * @param {number} params.latTo - 北東端の緯度
 * @param {number} params.lngFrom - 南西端の経度
 * @param {number} params.lngTo - 北東端の経度
 * @param {AbortSignal} [params.signal] - リクエスト中断用のシグナル
 * @returns {Promise<Property[]>} 表示範囲内の物件データ一覧
 * @throws {Error} API エラー時（response.ok でない場合）
 * @throws {DOMException} signal によって中断された場合（AbortError）
 */
export const fetchPropertiesInBounds = async ({
    type = "all",
    latFrom,
    latTo,
    lngFrom,
    lngTo,
    conditions,
    signal,
}) => {
    const params = new URLSearchParams({ latFrom, latTo, lngFrom, lngTo });
    // 条件検索(terms)から引き継いだ検索条件を付与する（budget/floor_plan/kodawari[] 等）
    if (conditions) {
        for (const [key, value] of conditions) {
            params.append(key, value);
        }
    }
    const response = await fetch(
        `/api/search/map/type/${type}?${params.toString()}`,
        { signal }
    );
    if (!response.ok) throw new Error(`APIエラー: ${response.status}`);
    const data = await response.json();
    return data.items;
};

/**
 * ids によって物件カード HTML を取得する
 * @param {Object} params
 * @param {Array<number|string>} params.ids - 物件IDの一覧
 * @param {"rent"|"buy"} [params.type="buy"] - 取引種別
 * @returns {Promise<string>} 物件カードの HTML 文字列
 * @throws {Error} API エラー時（response.ok でない場合）
 */
export const fetchPropertyCards = async ({ ids, type = "buy" }) => {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("id[]", id));

    const response = await fetch(
        `/api/search/map-cards/id?${params.toString()}&type=${type}`
    );
    if (!response.ok) throw new Error(`APIエラー: ${response.status}`);
    return await response.text();
};
